import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { post, get } from '../lib/api';

interface ImpersonatingInfo {
  originalAdminId: string;
  originalAdminEmail: string;
  originalTenantId: string;
}

interface User {
  id: string;
  tenantId: string;
  name: string;
  email: string;
  companyName?: string;
}

interface TenantInfo {
  id: string;
  name: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  isPlatformAdmin: boolean;
  impersonating: ImpersonatingInfo | null;
  myTenants: TenantInfo[] | null;
  login: (email: string, password: string) => Promise<void>;
  register: (data: { companyName: string; userName: string; email: string; password: string; acceptTerms: boolean; marketingSubscribed: boolean }) => Promise<void>;
  logout: () => void;
  loadMyTenants: () => Promise<TenantInfo[]>;
  switchTenant: (newTenantId: string) => Promise<void>;
  createCompany: (companyName: string, taxNumber?: string) => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const payload = token.split('.')[1];
    if (!payload) return {};
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return {};
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [impersonating, setImpersonating] = useState<ImpersonatingInfo | null>(null);
  const [myTenants, setMyTenants] = useState<TenantInfo[] | null>(null);

  function applyToken(t: string | null) {
    setToken(t);
    if (!t) {
      setIsPlatformAdmin(false);
      setImpersonating(null);
      return;
    }
    const payload = decodeJwtPayload(t);
    setIsPlatformAdmin(payload.isPlatformAdmin === true);
    setImpersonating((payload.impersonating as ImpersonatingInfo) || null);
  }

  useEffect(() => {
    const savedToken = localStorage.getItem('accessToken');
    const userData = localStorage.getItem('user');
    if (savedToken && userData) {
      try {
        setUser(JSON.parse(userData));
        applyToken(savedToken);
      } catch { /* invalid */ }
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    const data = await post<{ accessToken: string; refreshToken: string; user: User }>('/auth/login', { email, password });
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    localStorage.setItem('user', JSON.stringify(data.user));
    applyToken(data.accessToken);
    setUser(data.user);
  };

  const register = async (input: { companyName: string; userName: string; email: string; password: string }) => {
    const data = await post<{ accessToken: string; refreshToken: string; user: User }>('/auth/register', input);
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    localStorage.setItem('user', JSON.stringify(data.user));
    applyToken(data.accessToken);
    setUser(data.user);
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    applyToken(null);
    setUser(null);
    setMyTenants(null);
  };

  const loadMyTenants = useCallback(async (): Promise<TenantInfo[]> => {
    const data = await get<{ tenants: TenantInfo[] }>('/auth/my-tenants');
    setMyTenants(data.tenants);
    return data.tenants;
  }, []);

  const switchTenant = useCallback(async (newTenantId: string): Promise<void> => {
    const data = await post<{ accessToken: string; refreshToken: string; user: User }>('/auth/switch-tenant', { newTenantId });
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    localStorage.setItem('user', JSON.stringify(data.user));
    applyToken(data.accessToken);
    setUser(data.user);
    setMyTenants(null);
  }, []);

  const createCompany = useCallback(async (companyName: string, taxNumber?: string): Promise<void> => {
    const data = await post<{ accessToken: string; refreshToken: string; user: User }>('/auth/create-company', { companyName, taxNumber });
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    localStorage.setItem('user', JSON.stringify(data.user));
    applyToken(data.accessToken);
    setUser(data.user);
    setMyTenants(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loading, isPlatformAdmin, impersonating, myTenants, login, register, logout, loadMyTenants, switchTenant, createCompany }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
